import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  SortableContext,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { nanoid } from "nanoid";
import styled from "styled-components";

import { POST_TYPES } from "constants/PostsView";
import { FIELDS } from "constants/PostFormFields";
import PostFieldError from "components/Posts/PostForm/PostFieldError/PostFieldError";
import PostFieldCounter from "components/Posts/PostForm/PostFieldCounter/PostFieldCounter";
import { useOpenableState } from "libs/use-openable-state";
import type { MediaFilesErrors, MediaFilesWarning, Post } from "features/post";
import type {
  FormValidationErrors,
  FormValidationWarnings,
} from "shared/types/post";

import { getAccountPlatformType } from "@/entities/accounts/libs/account-platform";
import { cn } from "@/lib/utils";
import { isAutoPublishedRegularStory } from "utils/posts";

import { PostMediaFile } from "../../../media-library/types";
import { MediaModal } from "features/media-library/components/media-modal/media-modal";

import {
  PostSelectedAsset,
  PostSelectedAssetView,
} from "./post-selected-asset";
import { WarningsErrors } from "./assets-errors-warnings";

const MAX_MEDIA_FILES = 20;
const DRAGGING_Z_INDEX = 10;

export interface PostAssetProps extends PostMediaFile {
  name?: string;
  altText?: string;
  downloadUrl?: string;
  gifThumbnail?: string;
  favorite?: boolean;
}

interface PostSelectedAssetsProperties {
  post: Post;
  initialView?: PostSelectedAssetView;
  className?: string;
  errors: FormValidationErrors;
  warnings: FormValidationWarnings;
  disabled?: boolean;
  selectedAccountIds: string[];
  onChange(mediaFiles: PostAssetProps[]): void;
}

const getTypeFromUrl = (url?: string) => {
  return (
    url &&
    (/.mp4|.mov|.wmv|.avi|.flv|.avchd|.webm|.mpeg|.mpg|.m4v|.m4p|.qt|.swf/gi.test(
      url
    )
      ? "video"
      : /.pdf/gi.test(url)
        ? "document"
        : "image")
  );
};

const getPostType = (postType: string, storyPostType?: string | undefined) => {
  if (postType === POST_TYPES.STORY) {
    switch (storyPostType) {
      case "igtv": {
        return "IGTV";
      }
      case "reel": {
        return "Instagram Reel";
      }
      case "story":
      default: {
        return "Story";
      }
    }
  }

  switch (postType) {
    case POST_TYPES.IMAGE: {
      return "Image";
    }
    case POST_TYPES.VIDEO: {
      return "Video";
    }
    case POST_TYPES.MULTI_MEDIA: {
      return "Carousel";
    }
    case POST_TYPES.PHOTO: {
      return "Image";
    }
    case POST_TYPES.DOCUMENT: {
      return "Document/PDF";
    }
    case POST_TYPES.TEXT:
    default: {
      return "Text";
    }
  }
};

const getPreferredMediaView = (mediaCount: number): PostSelectedAssetView =>
  mediaCount > 1 ? "compressed" : "list";

const getInitialMediaCount = (post: Post) => {
  if (!post.type || post.type === POST_TYPES.TEXT) {
    return 0;
  }

  return post.mediaFiles?.length || 1;
};

// Assets usually have a stable backend key or URL. This WeakMap only covers
// the temporary edge case where a media object has neither, while still letting
// the browser garbage collect IDs when those asset objects fall out of state.
const getFallbackSortableId = (
  asset: PostAssetProps,
  fallbackSortableIds: WeakMap<PostAssetProps, string>
) => {
  const existingSortableId = fallbackSortableIds.get(asset);

  if (existingSortableId) {
    return existingSortableId;
  }

  const sortableId = `media-file-${nanoid()}`;
  fallbackSortableIds.set(asset, sortableId);

  return sortableId;
};

interface SortablePostSelectedAssetProperties {
  asset: PostAssetProps;
  draggableId: string;
  disabled?: boolean;
  onEdit(file: PostAssetProps): void;
  onRemove(): void;
  view: PostSelectedAssetView;
}

const SortablePostSelectedAsset = ({
  asset,
  draggableId,
  disabled,
  onEdit,
  onRemove,
  view,
}: SortablePostSelectedAssetProperties) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: draggableId,
    disabled,
  });

  return (
    <PostSelectedAsset
      asset={asset}
      onRemove={onRemove}
      onEdit={onEdit}
      disabled={disabled}
      isDragging={isDragging}
      view={view}
      draggableProps={{
        innerRef: setNodeRef,
        dragHandleProps: {
          ...attributes,
          ...listeners,
        },
        draggableProps: {
          style: {
            transform: CSS.Transform.toString(transform),
            transition,
            zIndex: isDragging ? DRAGGING_Z_INDEX : undefined,
          },
        },
      }}
    />
  );
};

export const PostSelectedAssets = (props: PostSelectedAssetsProperties) => {
  const {
    post,
    className = "",
    onChange,
    errors,
    warnings,
    disabled,
    selectedAccountIds,
    initialView,
  } = props;
  const MediaLibraryModal = useOpenableState();

  const mediaFilesWarnings = ((warnings?.[FIELDS.MEDIA]?.warnings as
    MediaFilesWarning | undefined) ?? {}) as MediaFilesWarning;

  const mediaFilesErrors = ((errors?.[FIELDS.MEDIA]?.errors as
    MediaFilesErrors | undefined) ?? {}) as MediaFilesErrors;

  const fallbackSortableIdsRef = useRef(new WeakMap<PostAssetProps, string>());
  const [mediaFiles, setMediaFiles] = useState<PostAssetProps[]>([]);
  const [postType, setPostType] = useState<string>("");
  const initialMediaCount = getInitialMediaCount(post);
  const [view, setView] = useState<PostSelectedAssetView>(
    () => initialView ?? getPreferredMediaView(initialMediaCount)
  );
  const hasMultipleMediaRef = useRef<boolean | null>(null);
  const reconcileMediaView = useCallback(
    (
      mediaCount: number,
      previousHasMultipleMedia = hasMultipleMediaRef.current
    ) => {
      const hasMultipleMedia = mediaCount > 1;

      if (
        (previousHasMultipleMedia === null && initialView === undefined) ||
        (previousHasMultipleMedia !== null &&
          hasMultipleMedia !== previousHasMultipleMedia)
      ) {
        setView(getPreferredMediaView(mediaCount));
      }

      hasMultipleMediaRef.current = hasMultipleMedia;
    },
    [initialView]
  );
  const getDraggableId = useCallback(
    // The ref container is stable for the component lifetime, and this callback
    // only reads its current WeakMap to mint fallback IDs, so an empty
    // dependency list is intentional here.
    (asset: PostAssetProps) =>
      asset._id ||
      asset.url ||
      asset.originalUrl ||
      getFallbackSortableId(asset, fallbackSortableIdsRef.current),
    []
  );
  const sortableIds = useMemo(
    () => mediaFiles.map(getDraggableId),
    [mediaFiles, getDraggableId]
  );
  // dnd-kit memoizes the sensor descriptors returned from useSensor/useSensors,
  // so keeping the hook calls inline is safe and keeps hook ordering obvious.
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        // Small threshold so clicks on edit/remove controls do not start a drag.
        distance: 4,
      },
    }),
    useSensor(KeyboardSensor, {
      // Keep dnd-kit's default keyboard accessibility/announcements while using
      // sortable-aware coordinates for left/right reordering.
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Enumerate individual post fields instead of [post] to avoid re-running
  // on every post reference change (e.g. VideoReelToggle updating videoPostType
  // creates a new post object via the reducer's { ...form.post } shallow copy).
  // post.mediaFiles and post.meta are reference-stable from Redux — only
  // reassigned in the FIELDS.MEDIA reducer case, so they change in sync.
  // Note: in-place mutations to mediaFiles items won't trigger this effect —
  // callers should always replace the array, not mutate items within it.
  useEffect(() => {
    let postMediaFiles: PostAssetProps[] = [];

    if (post.type && post.type !== POST_TYPES.TEXT) {
      if (post.mediaFiles?.length > 0) {
        postMediaFiles = [...(post.mediaFiles as PostAssetProps[])];
      }

      if (!post.mediaFiles?.length) {
        const originalUrl = post.originalUrl || post.origUrl;
        const type =
          post.type !== "story" && originalUrl
            ? post.type
            : getTypeFromUrl(originalUrl ?? undefined);

        postMediaFiles = [
          {
            _id: post._id,
            altText: post.altText,
            fileName: post.fileName ?? undefined,
            isVideo: post.isVideo,
            name: post.fileName ?? undefined,
            originalUrl: post.originalUrl || post.origUrl || undefined,
            thumbnailUrl: post.thumbnailUrl || "",
            type: type || undefined,
            url: post.url || "",
            meta: post.meta || {},
          },
        ];
      }
    }

    setMediaFiles(postMediaFiles);

    reconcileMediaView(postMediaFiles.length);
    setPostType(
      getPostType(post.type, post[FIELDS.STORY_TYPE] || undefined) || ""
    );
  }, [
    post.type,
    post.storyType,
    post.mediaFiles,
    post._id,
    post.url,
    post.originalUrl,
    post.origUrl,
    post.altText,
    post.fileName,
    post.isVideo,
    post.thumbnailUrl,
    post.meta,
    reconcileMediaView,
  ]);

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) {
      return;
    }

    const activeId = active.id as string;
    const overId = over.id as string;
    const oldIndex = sortableIds.indexOf(activeId);
    const newIndex = sortableIds.indexOf(overId);

    // The backing media array can change while a drag is in progress (for example,
    // if parent state refreshes after an edit/remove), so guard against stale IDs
    // and leave the order unchanged instead of interrupting the user.
    if (oldIndex < 0 || newIndex < 0) {
      return;
    }

    const items = arrayMove(mediaFiles, oldIndex, newIndex);

    setMediaFiles(items);
    onChange(items);
  };

  function handleRemove(index: number) {
    const newMediaFiles = [...mediaFiles];
    newMediaFiles.splice(index, 1);
    reconcileMediaView(newMediaFiles.length, mediaFiles.length > 1);
    onChange(newMediaFiles);
  }

  const handleEdit = (file: PostAssetProps, index: number) => {
    const newMediaFiles = [...mediaFiles];
    newMediaFiles[index] = file;
    onChange(newMediaFiles);
  };

  const handleSelectAssets = (newMediaFiles: PostAssetProps[]) => {
    const uniqueAssets = newMediaFiles.filter(
      (asset) => !mediaFiles.some((media) => media.url === asset.url)
    );
    const combinedMediaFiles = [...mediaFiles, ...uniqueAssets];

    reconcileMediaView(combinedMediaFiles.length, mediaFiles.length > 1);
    onChange(combinedMediaFiles);

    MediaLibraryModal.close();
  };

  const mediaLimit = isAutoPublishedRegularStory(post) ? 3 : MAX_MEDIA_FILES;
  const remainingMediaLimit = Math.max(mediaLimit - mediaFiles.length, 0);
  const isAddMediaDisabled =
    remainingMediaLimit === 0 || post.accounts?.length === 0;

  return (
    <PostSelectedAssetsRoot className={className}>
      <div className="overflow-hidden rounded-lg border-0 bg-white mb-4 shadow-[inset_0_0_0_1px_#e6e6e6,0_1px_2px_rgba(16,24,40,0.05)]">
        {mediaFiles.length > 0 && (
          <div className="flex h-12 min-h-12 box-border items-center justify-end gap-2 border-0 px-4 py-2 shadow-[inset_0_-1px_0_#e6e6e6]">
            <div
              className="inline-flex items-center gap-0.5 rounded-lg bg-neutral-50 p-0.5"
              role="group"
              aria-label="Media view"
            >
              {MEDIA_VIEWS.map((mediaView) => {
                const isSelected = view === mediaView.value;

                return (
                  <button
                    key={mediaView.value}
                    type="button"
                    aria-label={mediaView.label}
                    aria-pressed={isSelected}
                    title={`${mediaView.label} view`}
                    className={cn(
                      "inline-flex size-7 cursor-pointer items-center justify-center rounded-lg border-0 p-1 text-neutral-800 transition-colors hover:bg-white hover:text-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-blue-500 [&_i]:inline-flex [&_i]:size-5 [&_i]:items-center [&_i]:justify-center [&_i]:text-[16px]",
                      isSelected &&
                        "bg-white text-blue-500 shadow-[0_1px_2px_rgba(16,24,40,0.05)]"
                    )}
                    onClick={() => setView(mediaView.value)}
                  >
                    <i
                      className={`${isSelected ? "fa-solid" : "fa-regular"} ${mediaView.icon}`}
                      aria-hidden
                    />
                  </button>
                );
              })}
            </div>
            {!disabled && (
              <button
                type="button"
                data-testid="postAddMedia"
                className="inline-flex h-8 w-[124px] shrink-0 cursor-pointer items-center gap-2 whitespace-nowrap rounded-lg border border-solid border-[#e6e6e6] bg-white px-3 py-1.5 font-inter text-sm font-medium leading-5 text-neutral-800 shadow-[0_1px_2px_rgba(16,24,40,0.05)] hover:bg-neutral-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:cursor-not-allowed disabled:bg-neutral-100 disabled:text-neutral-400 [&_i]:inline-flex [&_i]:size-5 [&_i]:items-center [&_i]:justify-center [&_i]:text-[16px]"
                onClick={MediaLibraryModal.open}
                disabled={isAddMediaDisabled}
              >
                <i className="fa-regular fa-plus" aria-hidden="true" />
                Add media
              </button>
            )}
          </div>
        )}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={onDragEnd}
        >
          <MediaContainer
            $view={view}
            $empty={mediaFiles.length === 0}
            data-testid="media-picker-content"
          >
            <SortableContext items={sortableIds} strategy={rectSortingStrategy}>
              <DragContainer $view={view}>
                {mediaFiles.map((item, index) => {
                  const draggableId = getDraggableId(item);

                  return (
                    <SortablePostSelectedAsset
                      key={draggableId}
                      asset={item}
                      draggableId={draggableId}
                      onRemove={() => handleRemove(index)}
                      onEdit={(file) => handleEdit(file, index)}
                      disabled={disabled}
                      view={view}
                    />
                  );
                })}
              </DragContainer>
            </SortableContext>
            {mediaFiles.length === 0 && (
              <EmptyContainer>
                <button
                  type="button"
                  data-testid="postAddMedia"
                  className="inline-flex h-8 w-[124px] shrink-0 cursor-pointer items-center gap-2 whitespace-nowrap rounded-lg border border-solid border-[#e6e6e6] bg-white px-3 py-1.5 font-inter text-sm font-medium leading-5 text-neutral-800 shadow-[0_1px_2px_rgba(16,24,40,0.05)] hover:bg-neutral-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:cursor-not-allowed disabled:bg-neutral-100 disabled:text-neutral-400 [&_i]:inline-flex [&_i]:size-5 [&_i]:items-center [&_i]:justify-center [&_i]:text-[16px]"
                  onClick={MediaLibraryModal.open}
                  disabled={isAddMediaDisabled}
                >
                  <i className="fa-regular fa-plus" aria-hidden="true" />
                  Add media
                </button>
                <span>Select media assets from the Sked Library</span>
              </EmptyContainer>
            )}
          </MediaContainer>
        </DndContext>
        <AssetsDetails>
          {postType && <PostType>{postType} post</PostType>}
          <PostFieldCounter
            count={mediaFiles.length}
            max={mediaLimit}
            selectedPlatform={getAccountPlatformType(post.accounts?.[0])}
          >
            Media
          </PostFieldCounter>
        </AssetsDetails>
      </div>

      {/* TODO FIELDS.TYPE error mesesages for hotfix, need to refactor as FIELDS.MEDIA, please see WarningsErrors */}
      <PostFieldErrorStyled
        fields={[FIELDS.TYPE]}
        errors={errors}
        warnings={warnings}
      />

      <WarningsErrors
        warnings={mediaFilesWarnings}
        errors={mediaFilesErrors}
        files={mediaFiles}
      />

      <MediaModal
        show={MediaLibraryModal.isOpened}
        onHide={MediaLibraryModal.close}
        limit={remainingMediaLimit}
        onSelect={async (files) => {
          handleSelectAssets(files as PostAssetProps[]);
        }}
        selectedAccountIds={selectedAccountIds}
      />
    </PostSelectedAssetsRoot>
  );
};

const MEDIA_VIEWS: Array<{
  value: PostSelectedAssetView;
  label: string;
  icon: string;
}> = [
  { value: "list", label: "List", icon: "fa-list" },
  { value: "compressed", label: "Compressed", icon: "fa-grip" },
  { value: "grid", label: "Grid", icon: "fa-grid-2" },
];

const EmptyContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 116px;
  flex-direction: column;
  gap: 16px;
  padding: 24px;

  & > span {
    font-family: Inter, sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    color: #808080;
  }
`;

const MediaContainer = styled.div<{
  $view: PostSelectedAssetView;
  $empty: boolean;
}>`
  display: ${({ $empty }) => ($empty ? "block" : "flex")};
  align-items: flex-start;
  min-width: 0;
  padding: ${({ $view, $empty }) =>
    $empty
      ? "0"
      : $view === "grid"
        ? "14px"
        : $view === "compressed"
          ? "16px"
          : "0"};
  overflow-y: ${({ $view }) => ($view === "grid" ? "auto" : "visible")};
  max-height: ${({ $view }) => ($view === "grid" ? "320px" : "none")};
  border-bottom: 1px solid #e6e6e6;
`;

const DragContainer = styled.div<{ $view: PostSelectedAssetView }>`
  position: relative;
  display: ${({ $view }) => ($view === "grid" ? "grid" : "flex")};
  grid-template-columns: ${({ $view }) =>
    $view === "grid" ? "repeat(4, minmax(0, 1fr))" : "none"};
  flex: 1 1 auto;
  width: 100%;
  min-width: 0;
  flex-direction: ${({ $view }) => ($view === "list" ? "column" : "row")};
  flex-wrap: ${({ $view }) => ($view === "compressed" ? "wrap" : "nowrap")};
  height: max-content;
  gap: ${({ $view }) => ($view === "list" ? "0" : "8px")};
  background: transparent;
`;

const PostFieldErrorStyled = styled(PostFieldError)`
  margin-top: 10px;

  &:empty {
    display: none;
    margin-top: 0;
  }
`;

const AssetsDetails = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  box-sizing: border-box;
  height: 36px;
  min-height: 36px;
  gap: 16px;
  border: 0;
  padding: 8px 16px;
`;

const PostType = styled.div`
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-style: normal;
  color: #808080;
`;

const PostSelectedAssetsRoot = styled.div`
  ${PostFieldError} {
    margin-top: 10px;
  }

  > [data-testid="postErrorList"]:empty {
    display: none;
    margin-top: 0;
  }
`;
